'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Flex, Box, VStack, useBreakpointValue } from '@skin-seoul/design-system';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import Image from 'next/image';
import SliderDot from './components/SliderDot';
import { useProductService, useRegionCurrency } from '@skin-seoul/service';
import { useParams } from 'next/navigation';
const MOBILE_IMAGE_SIZE = 240;
const DESKTOP_IMAGE_SIZE = 410;
const THUMBNAIL_SIZE = 60;
const ProductImageGallerySection = () => {
  const {
    useGetProductsDetail
  } = useProductService();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const {
    data: {
      product
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const [mobileApi, setMobileApi] = useState<CarouselApi | null>(null);
  const [desktopApi, setDesktopApi] = useState<CarouselApi | null>(null);
  const [mobileSelectedIndex, setMobileSelectedIndex] = useState(0);
  const [desktopSelectedIndex, setDesktopSelectedIndex] = useState(0);
  const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set([0]));
  const isMobile = useBreakpointValue({
    base: true,
    tablet: false
  });
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const images = product.productImageUrls;
  const handleImageIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const index = parseInt(entry.target.getAttribute('data-index') || '0');
      if (entry.isIntersecting) {
        setVisibleImages(prev => new Set([...prev, index]));
      }
    });
  }, []);
  const handleMobileSelect = useCallback(() => {
    if (mobileApi) {
      setMobileSelectedIndex(mobileApi.selectedScrollSnap());
    }
  }, [mobileApi]);
  const handleDesktopSelect = useCallback(() => {
    if (desktopApi) {
      setDesktopSelectedIndex(desktopApi.selectedScrollSnap());
    }
  }, [desktopApi]);
  const handleMobileDotClick = useCallback((index: number) => {
    mobileApi?.scrollTo(index);
  }, [mobileApi]);
  const handleThumbnailClick = useCallback((index: number) => {
    desktopApi?.scrollTo(index);
  }, [desktopApi]);
  useEffect(() => {
    const observer = new IntersectionObserver(handleImageIntersection, {
      rootMargin: '50px',
      threshold: 0.1
    });
    imageRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });
    return () => observer.disconnect();
  }, [handleImageIntersection, images.length]);
  useEffect(() => {
    if (!mobileApi) {
      return;
    }
    setMobileSelectedIndex(mobileApi.selectedScrollSnap());
    mobileApi.on('select', handleMobileSelect);
    return () => {
      mobileApi.off('select', handleMobileSelect);
    };
  }, [mobileApi, handleMobileSelect]);
  useEffect(() => {
    if (!desktopApi) {
      return;
    }
    setDesktopSelectedIndex(desktopApi.selectedScrollSnap());
    desktopApi.on('select', handleDesktopSelect);
    return () => {
      desktopApi.off('select', handleDesktopSelect);
    };
  }, [desktopApi, handleDesktopSelect]);
  useEffect(() => {
    if (thumbnailContainerRef.current && images.length > 1) {
      const thumbnailHeight = THUMBNAIL_SIZE + 8;
      const scrollTop = desktopSelectedIndex * thumbnailHeight;
      thumbnailContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  }, [desktopSelectedIndex, images.length]);
  const renderImage = useCallback((image: string, index: number, size: number, isThumbnail = false) => {
    const shouldLoad = isMobile || isThumbnail || visibleImages.has(index) || index === 0;
    const priority = index === 0 || isThumbnail || isMobile;
    return <Box ref={(el: HTMLDivElement | null) => imageRefs.current[index] = el} data-index={index} width={`${size}px`} height={`${size}px`} position="relative" overflow="hidden" display="flex" alignItems="center" justifyContent="center">
          {shouldLoad ? <Image src={image} alt={`${product.name} - Image ${index + 1}`} width={size} height={size} priority={priority} loading={priority ? 'eager' : 'lazy'} style={{
        objectFit: 'contain',
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%'
      }} /> : <Box width="100%" height="100%" backgroundColor="white" display="flex" alignItems="center" justifyContent="center">
              <Box width="20px" height="20px" borderRadius="50%" backgroundColor="neutral.300" />
            </Box>}
        </Box>;
  }, [isMobile, visibleImages, product.name]);
  return <Box position="relative" background="white" padding={{
    base: 4,
    tablet: 6
  }} borderRadius={8} width="100%" height="100%" data-sentry-element="Box" data-sentry-component="ProductImageGallerySection" data-sentry-source-file="ProductImageGallerySection.tsx">
      {/* Mobile Layout */}
      <VStack gap={{
      base: 4,
      tablet: 8
    }} width="100%" height="100%" alignItems="center" justifyContent="center" display={{
      base: 'flex',
      tablet: 'none'
    }} data-sentry-element="VStack" data-sentry-source-file="ProductImageGallerySection.tsx">
        <Box width={`${MOBILE_IMAGE_SIZE}px`} height={`${MOBILE_IMAGE_SIZE}px`} position="relative" borderRadius={12} overflow="hidden" transition="height 0.2s ease" data-sentry-element="Box" data-sentry-source-file="ProductImageGallerySection.tsx">
          <Carousel opts={{
          loop: true,
          align: 'center'
        }} setApi={setMobileApi} style={{
          height: '100%'
        }} data-sentry-element="Carousel" data-sentry-source-file="ProductImageGallerySection.tsx">
            <Carousel.Content style={{
            height: '100%'
          }} data-sentry-element="Carousel.Content" data-sentry-source-file="ProductImageGallerySection.tsx">
              {images.map((image: string, index: number) => <Carousel.Item key={index} style={{
              paddingLeft: '0',
              height: '100%'
            }}>
                  {renderImage(image, index, MOBILE_IMAGE_SIZE)}
                </Carousel.Item>)}
            </Carousel.Content>
          </Carousel>
        </Box>

        {images.length > 1 && <Box display={{
        base: 'block',
        tablet: 'none'
      }} width="100%">
            <Flex justifyContent="center" gap={1}>
              {images.map((_, index) => <SliderDot key={index} isSelected={index === mobileSelectedIndex} onClick={() => handleMobileDotClick(index)} size="md" />)}
            </Flex>
          </Box>}
      </VStack>

      {/* Desktop Layout */}
      <Flex display={{
      base: 'none',
      tablet: 'flex'
    }} gap={16} alignItems="center" justifyContent="center" width="100%" height="100%" data-sentry-element="Flex" data-sentry-source-file="ProductImageGallerySection.tsx">
        {images.length > 1 && <VStack ref={thumbnailContainerRef} gap={2} height={`${DESKTOP_IMAGE_SIZE}px`} overflowY="auto" paddingRight="4px" justifyContent={images.length < 5 ? 'center' : 'flex-start'} css={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
            {images.map((image, index) => <Box key={index} width={`${THUMBNAIL_SIZE}px`} height={`${THUMBNAIL_SIZE}px`} borderRadius={4} overflow="hidden" border="1px solid" borderColor={index === desktopSelectedIndex ? 'primary.400' : 'transparent'} cursor="pointer" transition="all 0.2s" _hover={{
          borderColor: 'primary.300'
        }} onClick={() => handleThumbnailClick(index)} position="relative" flexShrink={0}>
                {renderImage(image, index, THUMBNAIL_SIZE, true)}
              </Box>)}
          </VStack>}

        <Box width={`${DESKTOP_IMAGE_SIZE}px`} height={`${DESKTOP_IMAGE_SIZE}px`} position="relative" borderRadius={12} overflow="hidden" transition="height 0.2s ease" data-sentry-element="Box" data-sentry-source-file="ProductImageGallerySection.tsx">
          <Carousel opts={{
          loop: true,
          align: 'center'
        }} setApi={setDesktopApi} style={{
          height: '100%'
        }} data-sentry-element="Carousel" data-sentry-source-file="ProductImageGallerySection.tsx">
            <Carousel.Content style={{
            height: '100%'
          }} data-sentry-element="Carousel.Content" data-sentry-source-file="ProductImageGallerySection.tsx">
              {images.map((image: string, index: number) => <Carousel.Item key={index} style={{
              paddingLeft: '0',
              height: '100%'
            }}>
                  {renderImage(image, index, DESKTOP_IMAGE_SIZE)}
                </Carousel.Item>)}
            </Carousel.Content>
          </Carousel>
        </Box>
      </Flex>
    </Box>;
};
export default ProductImageGallerySection;